import { FormEvent, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Check,
  ChevronLeft,
  Loader2,
  Mail,
  MessageCircle,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import { useContactForm } from '../context/ContactFormContext';
import { matchCourseByBudget, MatchableCourse } from '../utils/matchCourse';

interface QuizQuestion {
  q: string;
  options: string[];
}

const BUDGET_QUESTION_INDEX = 3;
const INTEREST_QUESTION_INDEX = 4;

const TELEGRAM_URL = 'https://t.me/RedLevelCrypto';
const EMAIL_URL = 'mailto:redlevelsupport@icloud.com';

const ContactFormModal = () => {
  const { t, i18n } = useTranslation();
  const { isOpen, close, courseName, instanceId, mode } = useContactForm();
  const isCallback = mode === 'callback';

  const questions = t('contactForm.questions', { returnObjects: true }) as QuizQuestion[];
  const courses = t('courses.items', { returnObjects: true }) as MatchableCourse[];

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [telegram, setTelegram] = useState('');
  const [answers, setAnswers] = useState<string[]>([]);
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);

  const recommendedCourse = useMemo(() => {
    if (isCallback) return null;
    const budgetAnswer = answers[BUDGET_QUESTION_INDEX];
    if (!budgetAnswer) return null;
    return matchCourseByBudget(courses, budgetAnswer, answers[INTEREST_QUESTION_INDEX]);
  }, [answers, courses, isCallback]);

  const totalQuiz = questions.length;
  const totalSteps = isCallback ? 1 : totalQuiz + 2;
  const isNameStep = !isCallback && step === 0;
  const isQuizStep = !isCallback && step > 0 && step <= totalQuiz;
  const isContactsStep = isCallback || step === totalQuiz + 1;
  const quizIndex = step - 1;
  const currentQuestion = isQuizStep ? questions[quizIndex] : null;
  const progress = success ? 100 : Math.round(((step + 1) / totalSteps) * 100);

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setName('');
      setEmail('');
      setTelegram('');
      setAnswers([]);
      setWebsite('');
      setErrors({});
      setSubmitting(false);
      setSubmitError('');
      setSuccess(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, instanceId]);

  const validateName = (value: string) => {
    if (!value.trim()) {
      return t('contactForm.required');
    }
    return '';
  };

  const validateEmail = (value: string) => {
    if (!value.trim()) {
      return t('contactForm.required');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      return t('contactForm.invalidEmail');
    }
    return '';
  };

  const validateTelegram = (value: string) => {
    if (!value.trim()) {
      return t('contactForm.required');
    }
    const cleaned = value
      .trim()
      .replace(/^@/, '')
      .replace(/^(https?:\/\/)?t\.me\//i, '');
    if (!/^[A-Za-z0-9_]{5,32}$/.test(cleaned)) {
      return t('contactForm.invalidTelegram');
    }
    return '';
  };

  const goToQuiz = () => {
    const nameError = validateName(name);
    if (nameError) {
      setErrors({ name: nameError });
      return;
    }
    setErrors({});
    setStep(1);
  };

  const selectAnswer = (option: string) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[quizIndex] = option;
      return next;
    });

    window.setTimeout(() => {
      setStep((prev) => prev + 1);
    }, 220);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const nextErrors = {
      name: validateName(name),
      email: validateEmail(email),
      telegram: validateTelegram(telegram),
    };

    setErrors(nextErrors);
    setSubmitError('');

    if (nextErrors.name || nextErrors.email || nextErrors.telegram) {
      return;
    }

    setSubmitting(true);

    try {
      const matchedName = recommendedCourse?.name || '';
      const payload = {
        name: name.trim(),
        email: email.trim(),
        telegram: telegram.trim(),
        course: courseName || matchedName,
        recommendedCourse: matchedName,
        recommendedPrice: recommendedCourse?.price ?? null,
        formType: isCallback ? 'callback' : 'quiz',
        language: i18n.language,
        website,
        answers: isCallback
          ? []
          : questions.map((question, index) => ({
              question: question.q,
              answer: answers[index] || '',
            })),
      };

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({ ok: false }));

      if (!response.ok || !result.ok) {
        throw new Error('submit_failed');
      }

      setSuccess(true);
    } catch {
      setSubmitError(t('contactForm.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-crypto-dark border text-white placeholder:text-gray-500 outline-none transition-colors';

  const fieldClass = (hasError: boolean) =>
    `${inputClass} ${hasError ? 'border-crypto-red' : 'border-crypto-red/20 focus:border-crypto-red/60'}`;

  const DirectButtons = useMemo(
    () => (
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-wider text-white text-center">
          {t('contactForm.orDirect')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#229ED9]/15 border border-[#229ED9]/40 text-white font-medium hover:bg-[#229ED9]/25 transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            {t('contactForm.writeTelegram')}
          </a>
          <a
            href={EMAIL_URL}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-crypto-dark border border-crypto-red/20 text-white font-medium hover:border-crypto-red/50 transition-colors"
          >
            <Mail className="w-4 h-4 text-crypto-red" />
            {t('contactForm.writeEmail')}
          </a>
        </div>
      </div>
    ),
    [t]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="contact-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
          />

          <div
            key="contact-dialog"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={close}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              onClick={(event) => event.stopPropagation()}
              className="w-full max-w-lg max-h-[90vh] overflow-y-auto"
              data-modal-lock
            >
              <div className="relative bg-crypto-card border border-crypto-red/30 rounded-3xl p-6 sm:p-8">
                <button
                  onClick={close}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
                  aria-label={t('contactForm.close')}
                >
                  <X className="w-5 h-5" />
                </button>

                {!success && !isCallback && (
                  <div className="h-1.5 w-full bg-crypto-dark rounded-full mb-6 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-crypto-red to-crypto-gradient-end transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}

                {success ? (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-crypto-success/10 flex items-center justify-center">
                      <Check className="w-8 h-8 text-crypto-success" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {t('contactForm.successTitle')}
                    </h3>
                    <p className="text-gray-400 mb-6">{t('contactForm.successText')}</p>
                    {recommendedCourse && (
                      <div className="mb-8 text-left p-4 rounded-2xl bg-crypto-dark border border-crypto-red/25">
                        <p className="text-xs uppercase tracking-wider text-crypto-red mb-1">
                          {t('contactForm.recommendedLabel')}
                        </p>
                        <p className="text-lg font-bold text-white">{recommendedCourse.name}</p>
                        <p className="text-gray-400 text-sm mt-1">
                          {t('contactForm.recommendedPrice', { price: recommendedCourse.price })}
                        </p>
                      </div>
                    )}
                    <button
                      onClick={close}
                      className="w-full py-4 bg-crypto-red hover:bg-crypto-redDark text-white font-bold rounded-xl transition-colors"
                    >
                      {t('contactForm.close')}
                    </button>
                  </div>
                ) : (
                  <>
                    {isNameStep && (
                      <div>
                        <div className="w-14 h-14 bg-crypto-red/10 rounded-2xl flex items-center justify-center mb-5">
                          <Sparkles className="w-7 h-7 text-crypto-red" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {t('contactForm.title')}
                        </h3>
                        <p className="text-gray-400 mb-6">{t('contactForm.subtitle')}</p>

                        <label className="block text-sm text-gray-300 mb-2">
                          {t('contactForm.nameLabel')} *
                        </label>
                        <input
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            if (errors.name) setErrors({});
                          }}
                          placeholder={t('contactForm.namePlaceholder')}
                          className={fieldClass(Boolean(errors.name))}
                          autoComplete="name"
                        />
                        {errors.name && (
                          <p className="text-crypto-red text-sm mt-2">{errors.name}</p>
                        )}

                        <button
                          onClick={goToQuiz}
                          className="mt-6 flex items-center justify-center gap-2 w-full py-4 bg-crypto-red hover:bg-crypto-redDark text-white font-bold rounded-xl transition-colors"
                        >
                          {t('contactForm.startQuiz')}
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </div>
                    )}

                    {isQuizStep && currentQuestion && (
                      <div>
                        {step > 0 && (
                          <button
                            onClick={() => setStep((prev) => prev - 1)}
                            className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            {t('contactForm.back')}
                          </button>
                        )}
                        <p className="text-xs uppercase tracking-wider text-crypto-red mb-2">
                          {t('contactForm.questionOf', { current: quizIndex + 1, total: totalQuiz })}
                        </p>
                        <h3 className="text-xl font-bold text-white mb-6">{currentQuestion.q}</h3>
                        <div className="space-y-3">
                          {currentQuestion.options.map((option) => {
                            const selected = answers[quizIndex] === option;
                            return (
                              <button
                                key={option}
                                onClick={() => selectAnswer(option)}
                                className={`w-full text-left px-4 py-4 rounded-xl border transition-all ${
                                  selected
                                    ? 'border-crypto-red bg-crypto-red/15 text-white'
                                    : 'border-crypto-red/15 bg-crypto-dark text-gray-200 hover:border-crypto-red/40'
                                }`}
                              >
                                {option}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {isContactsStep && (
                      <form onSubmit={handleSubmit}>
                        {!isCallback && (
                          <button
                            type="button"
                            onClick={() => setStep(totalQuiz)}
                            className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            {t('contactForm.back')}
                          </button>
                        )}
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {isCallback
                            ? t('contactForm.callbackTitle')
                            : t('contactForm.matchTitle')}
                        </h3>
                        <p className="text-gray-400 mb-4">
                          {isCallback
                            ? t('contactForm.callbackSubtitle')
                            : t('contactForm.matchSubtitle')}
                        </p>
                        {isCallback && courseName && (
                          <p className="mb-4 text-sm text-crypto-red font-semibold">{courseName}</p>
                        )}

                        {recommendedCourse && (
                          <div className="mb-6 p-4 rounded-2xl bg-crypto-dark border border-crypto-red/30">
                            <p className="text-xs uppercase tracking-wider text-crypto-red mb-1">
                              {t('contactForm.recommendedLabel')}
                            </p>
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-lg font-bold text-white">{recommendedCourse.name}</p>
                                <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                                  {recommendedCourse.description}
                                </p>
                              </div>
                              <p className="shrink-0 text-crypto-red font-bold">
                                {t('contactForm.recommendedPrice', {
                                  price: recommendedCourse.price,
                                })}
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm text-gray-300 mb-2">
                              {t('contactForm.nameLabel')} *
                            </label>
                            <input
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder={t('contactForm.namePlaceholder')}
                              className={fieldClass(Boolean(errors.name))}
                              autoComplete="name"
                            />
                            {errors.name && (
                              <p className="text-crypto-red text-sm mt-2">{errors.name}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm text-gray-300 mb-2">
                              {t('contactForm.emailLabel')} *
                            </label>
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder={t('contactForm.emailPlaceholder')}
                              className={fieldClass(Boolean(errors.email))}
                              autoComplete="email"
                            />
                            {errors.email && (
                              <p className="text-crypto-red text-sm mt-2">{errors.email}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm text-gray-300 mb-2">
                              {t('contactForm.telegramLabel')} *
                            </label>
                            <input
                              value={telegram}
                              onChange={(e) => setTelegram(e.target.value)}
                              placeholder={t('contactForm.telegramPlaceholder')}
                              className={fieldClass(Boolean(errors.telegram))}
                              autoComplete="off"
                            />
                            {errors.telegram && (
                              <p className="text-crypto-red text-sm mt-2">{errors.telegram}</p>
                            )}
                          </div>
                          <input
                            type="text"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            className="hidden"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                          />
                        </div>

                        {submitError && (
                          <p className="text-crypto-red text-sm mt-4">{submitError}</p>
                        )}

                        <button
                          type="submit"
                          disabled={submitting}
                          className="mt-6 flex items-center justify-center gap-2 w-full py-4 bg-crypto-red hover:bg-crypto-redDark disabled:opacity-70 text-white font-bold rounded-xl transition-colors"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              {t('contactForm.submitting')}
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5" />
                              {t('contactForm.submit')}
                            </>
                          )}
                        </button>
                      </form>
                    )}

                    <div className="mt-8 pt-6 border-t border-crypto-red/10">{DirectButtons}</div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactFormModal;
